import React from 'react';
import PropTypes from 'prop-types';

import t from '../../../labels/labelling-utils';
import { makeClassNames, IClassNames } from '../../../utils/styles';
import IconChronometer from '../icons/icon-chronometer';
import { IComponent } from '../../../typings/generic-component';
import { MatchPhase, MatchStatusList, MatchStatusType, nonLinearCompetitionPhasesArray } from '../../../typings/match';
import { getMatchStatusLabel, isBetweenPhases, isLive, isPreMatch } from '../../../utils/match-status';
import Text from '../text/text';

import './match-status.scss';

export type MatchStatusVariant = 'Default' | 'Wrapped';

export interface IMatchStatus extends IComponent {
  backgroundColor?: boolean;
  chrono?: boolean;
  dataSize?: boolean;
  label?: string;
  live?: boolean;
  matchTime?: string;
  red?: boolean;
  blinking?: boolean;
  variant?: MatchStatusVariant;
  status?: MatchStatusType;
  date?: string;
  matchPhase?: MatchPhase;
}

const classNames = (
  red: boolean,
  backgroundColor: boolean,
  dataSize: boolean,
  matchTime?: string,
  variant?: MatchStatusVariant,
  status?: MatchStatusType
): IClassNames =>
  makeClassNames({
    matchStatus: [
      'match-status',
      red && 'match-status--red',
      backgroundColor && 'match-status--bg',
      dataSize && 'match-status--medium',
      variant === 'Wrapped' && 'match-status-wrapped',
    ],
    live: 'match-status__live',
    label: [
      'match-status__label',
      matchTime && 'match-status__label-time',
      variant === 'Wrapped' && status && !isLive(status) && 'match-status__label--bg',
    ],
    matchTime: 'match-status__time',
    dot: 'match-status__dot',
    blinking: 'match-status__blinking',
    wrapper: [status && isBetweenPhases(status) ? 'match-status__wrapper-btp' : 'match-status__wrapper'],
    preMatchLabel: 'match-status-wrapped__time',
    container: 'match-status-wrapped__container',
  });

/**
 * If label prop is missing, the component will be empty
 */
const MatchStatus: React.FC<IMatchStatus> = ({
  backgroundColor = false,
  blinking = false,
  chrono = false,
  className,
  dataSize = false,
  label,
  live = false,
  matchTime,
  red = false,
  variant = 'Default',
  status,
  matchPhase,
  date,
}) => {
  const cn = classNames(red, backgroundColor, dataSize, matchTime, variant, status);

  return (
    <div className={[cn.matchStatus, className].filter(Boolean).join(' ')} aria-label="Stato partita" role="status">
      {variant === 'Wrapped' && status ? (
        <React.Fragment>
          {isLive(status) && !isBetweenPhases(status) && (
            <div className={cn.container}>
              <div className={cn.wrapper}>
                <span className={cn.live}> {t('match.status.live')}</span>
                <span className={cn.label}>{getMatchStatusLabel(status, true)}</span>
              </div>
              {matchTime && (
                <span className={cn.matchTime} aria-label="Minuto">
                  {matchTime}
                </span>
              )}
            </div>
          )}
          {isBetweenPhases(status) && (
            <div className={cn.wrapper}>
              <span className={cn.live}> {t('match.status.live')}</span>
              <span className={cn.label}>{getMatchStatusLabel(status, true)}</span>
            </div>
          )}
          {!isLive(status) && !isPreMatch(status) && (
            <span className={cn.label}>{getMatchStatusLabel(status, true)}</span>
          )}
          {isPreMatch(status) && (
            <div className={cn.preMatchLabel}>
              {matchPhase && <Text fontSize="16">{matchPhase}</Text>}
              <Text fontSize="16">{date}</Text>
            </div>
          )}
        </React.Fragment>
      ) : (
        <React.Fragment>
          {live && (
            <React.Fragment>
              {blinking && (
                <div className={cn.dot}>
                  <span className={cn.blinking}>&nbsp;</span>
                </div>
              )}
              <span className={cn.live}> {t('match.status.live')}</span>
            </React.Fragment>
          )}
          {label && !matchTime && (
            <React.Fragment>
              <span className={cn.label} data-status={live && !chrono ? 'onlyLive' : ''}>
                {chrono && <IconChronometer size="xxsmall" color="blue" />}
                {label}
              </span>
            </React.Fragment>
          )}
          {label && matchTime && (
            <React.Fragment>
              <span className={cn.matchTime}>
                {chrono && <IconChronometer size="xxsmall" color="blue" />}
                {matchTime}
              </span>
              <span className={cn.label} data-status={live && !chrono ? 'onlyLive' : ''}>
                {label}
              </span>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchStatus.propTypes = {
    backgroundColor: PropTypes.bool,
    blinking: PropTypes.bool,
    chrono: PropTypes.bool,
    className: PropTypes.string,
    dataSize: PropTypes.bool,
    date: PropTypes.string,
    label: PropTypes.string,
    live: PropTypes.bool,
    matchPhase: PropTypes.oneOf<MatchPhase>(nonLinearCompetitionPhasesArray),
    matchTime: PropTypes.string,
    red: PropTypes.bool,
    status: PropTypes.oneOf<MatchStatusType>(MatchStatusList),
  };
}

export default MatchStatus;
